import React from "react";
import styled from "styled-components";
import { EmailVersion } from "../../../../services/EmailSerivce";

// ---------------------------------------------------------------------------
// Styled
// ---------------------------------------------------------------------------

const Overlay = styled.div`
	position: fixed;
	inset: 0;
	background: rgba(0, 0, 0, 0.45);
	display: flex;
	align-items: center;
	justify-content: center;
	z-index: 1400;
`;

const Modal = styled.div`
	background: #fff;
	border-radius: 12px;
	box-shadow: 0 8px 40px rgba(0, 0, 0, 0.18);
	width: 560px;
	max-width: 96vw;
	max-height: 80vh;
	display: flex;
	flex-direction: column;
	font-family: -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif;
`;

const ModalHeader = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	padding: 16px 20px;
	border-bottom: 1px solid #e3e8ef;
	flex-shrink: 0;
`;

const ModalTitle = styled.h2`
	margin: 0;
	font-size: 15px;
	font-weight: 700;
	color: #1e293b;
`;

const CloseBtn = styled.button`
	border: none;
	background: transparent;
	cursor: pointer;
	font-size: 18px;
	color: #64748b;
	padding: 2px 6px;
	border-radius: 6px;
	line-height: 1;

	&:hover {
		background: #f1f5f9;
		color: #1e293b;
	}
`;

const VersionList = styled.div`
	overflow-y: auto;
	flex: 1;
	padding: 12px 16px;
	display: flex;
	flex-direction: column;
	gap: 8px;
`;

const VersionRow = styled.div`
	display: flex;
	align-items: center;
	gap: 10px;
	padding: 10px 12px;
	border-radius: 8px;
	border: 1px solid #e3e8ef;
	background: #f8fafc;
`;

const VersionMeta = styled.div`
	flex: 1;
	min-width: 0;
`;

const VersionName = styled.div`
	font-size: 13px;
	font-weight: 600;
	color: #1e293b;
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
`;

const VersionDate = styled.div`
	font-size: 12px;
	color: #64748b;
	margin-top: 2px;
`;

interface StatusBadgeProps {
	status: "draft" | "published";
}

const StatusBadge = styled.span<StatusBadgeProps>`
	flex-shrink: 0;
	font-size: 11px;
	font-weight: 700;
	padding: 3px 9px;
	border-radius: 999px;
	background: ${({ status }) => (status === "published" ? "#dcfce7" : "#fef9c3")};
	color: ${({ status }) => (status === "published" ? "#15803d" : "#92400e")};
`;

const RestoreBtn = styled.button`
	flex-shrink: 0;
	border: 1px solid #005e9e;
	background: transparent;
	color: #005e9e;
	font-size: 12px;
	font-weight: 600;
	padding: 5px 12px;
	border-radius: 6px;
	cursor: pointer;
	transition: background 0.15s;

	&:hover {
		background: #e8f2fa;
	}
`;

const EmptyState = styled.div`
	padding: 32px;
	text-align: center;
	color: #94a3b8;
	font-size: 14px;
`;

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export interface VersionHistoryModalProps {
	versions: EmailVersion[];
	onRestore: (versionId: number) => void;
	onClose: () => void;
}

function formatDate(iso: string): string {
	const date = new Date(iso);
	// new Date(undefined/garbage) does not throw — it yields an Invalid Date.
	if (isNaN(date.getTime())) return "—";
	return date.toLocaleString("vi-VN", {
		day: "2-digit",
		month: "2-digit",
		year: "numeric",
		hour: "2-digit",
		minute: "2-digit",
	});
}

export function VersionHistoryModal({ versions, onRestore, onClose }: VersionHistoryModalProps): JSX.Element {
	return (
		<Overlay
			onClick={(e) => {
				if (e.target === e.currentTarget) onClose();
			}}>
			<Modal>
				<ModalHeader>
					<ModalTitle>Lịch sử phiên bản</ModalTitle>
					<CloseBtn
						onClick={onClose}
						aria-label="Close">
						✕
					</CloseBtn>
				</ModalHeader>
				<VersionList>
					{versions.length === 0 ? (
						<EmptyState>Chưa có phiên bản nào.</EmptyState>
					) : (
						versions.map((v) => (
							<VersionRow key={v.id}>
								<VersionMeta>
									<VersionName>{v.name || `Phiên bản #${v.id}`}</VersionName>
									<VersionDate>{formatDate(v.createdAt)}</VersionDate>
								</VersionMeta>
								<StatusBadge status={v.status}>
									{v.status === "published" ? "Đã xuất bản" : "Nháp"}
								</StatusBadge>
								<RestoreBtn
									type="button"
									onClick={() => onRestore(v.id)}>
									Khôi phục
								</RestoreBtn>
							</VersionRow>
						))
					)}
				</VersionList>
			</Modal>
		</Overlay>
	);
}
